import React, { useEffect, useRef, useState } from "react";
import { usePolling } from "../lib/usePolling";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, BellRinging, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { pushSupported, registerServiceWorker, isPushEnabled, enablePush } from "../lib/push";
import { isPro as isProUser } from "./ProGate";

export const NotificationBell = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pushOn, setPushOn] = useState(false);
  const ref = useRef(null);

  const isPro = isProUser(user);

  const load = (signal) => {
    if (!isPro) return Promise.resolve();
    return api.get("/notifications", { signal }).then((r) => {
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    }).catch(() => {});
  };

  useEffect(() => {
    if (!isPro) return;
    registerServiceWorker();
    isPushEnabled().then(setPushOn).catch(() => {});
  }, [isPro]);
  usePolling(load, 60000, [isPro]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!isPro) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      api.post("/notifications/read").then(() => setUnread(0)).catch(() => {});
    }
  };

  const handleEnable = async () => {
    if (!pushSupported()) { toast.error(t("notif.unsupported")); return; }
    const res = await enablePush();
    if (res.ok) { setPushOn(true); toast.success(t("notif.activated")); }
    else if (res.reason === "denied") toast.error(t("notif.denied"));
    else toast.error(t("notif.enable_error"));
  };

  const timeAgo = (iso) => {
    try {
      const diff = (Date.now() - new Date(iso).getTime()) / 60000;
      if (diff < 60) return t("notif.ago_min", { n: Math.max(1, Math.round(diff)) });
      if (diff < 1440) return t("notif.ago_hour", { n: Math.round(diff / 60) });
      return t("notif.ago_day", { n: Math.round(diff / 1440) });
    } catch (e) { return ""; }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={toggle} type="button"
        aria-label={unread > 0 ? t("notif.aria_unread", { count: unread, defaultValue: "Notifications, {{count}} non lues" }) : t("notif.aria", "Notifications")}
        className="relative w-8 h-8 flex items-center justify-center rounded-full border border-white/10 bg-white/5">
        {unread > 0 ? <BellRinging size={16} weight="fill" color="#D4AF37" /> : <Bell size={16} color="rgba(255,255,255,0.6)" />}
        {unread > 0 && (
          <span data-testid="notif-unread-badge"
            className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-extrabold leading-none"
            style={{ boxShadow: "0 0 6px rgba(239,68,68,0.8)" }}>
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel"
          className="fixed top-16 right-3 w-[calc(100vw-1.5rem)] max-w-[340px] hm-card p-0 z-[60] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="text-xs font-bold uppercase tracking-wider text-white/70">{t("notif.title")}</span>
            {!pushOn && (
              <button data-testid="notif-enable-push" onClick={handleEnable}
                className="text-[10px] font-bold text-gold border border-gold/40 rounded-full px-2 py-0.5">
                {t("notif.enable")}
              </button>
            )}
            {pushOn && <span className="text-[10px] text-perf-excellent font-bold">{t("notif.enabled")}</span>}
          </div>
          <div className="max-h-80 overflow-auto no-scrollbar">
            {items.length === 0 ? (
              <p className="text-center text-white/40 text-xs py-8 px-4" data-testid="notif-empty">
                {t("notif.empty")}
              </p>
            ) : items.map((n) => (
              <button key={n.notif_id} data-testid="notif-item"
                onClick={() => { setOpen(false); if (n.fixture_id) navigate(`/vf/${n.fixture_id}`); }}
                className="w-full flex items-start gap-2 px-4 py-3 border-b border-white/5 hover:bg-white/5 text-left">
                <Lightning size={16} weight="fill" color="#00E676" className="mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-bold text-white truncate">{n.title}</p>
                  <p className="text-[11px] text-white/60 truncate">{n.body}</p>
                  <p className="text-[9px] text-white/30 mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
